import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  Text,
} from '@backstage/ui';
import { RiDeleteBinLine } from '@remixicon/react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import styles from './destroyInfrastructureButton.module.css';

export const DestroyInfrastructureButton = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const annotations = entity.metadata.annotations || {};

  const createdBy = annotations['infrastructure.backstage.io/created-by'];
  const templateType = annotations['infrastructure.backstage.io/type'];
  const envId = annotations['infrastructure.backstage.io/env-id'];

  const isInfrastructure = templateType && createdBy && envId;

  useEffect(() => {
    if (!snackbar.open) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      setSnackbar(prev => ({ ...prev, open: false }));
    }, 6000);
    return () => window.clearTimeout(id);
  }, [snackbar.open]);

  if (!isInfrastructure) {
    return null;
  }

  const createdBySanitized = createdBy?.replace(/^user:[^/]+\//, '') || createdBy;
  const statePrefix = `${templateType}/${createdBySanitized}/${envId}`;

  const getProviderName = () => {
    if (templateType?.startsWith('aws-')) return 'AWS';
    if (templateType?.startsWith('gcp-')) return 'GCP';
    if (templateType?.startsWith('azure-')) return 'Azure';
    return 'cloud';
  };

  const handleDestroy = async () => {
    setLoading(true);

    try {
      const backendUrl = config.getString('backend.baseUrl');

      const response = await fetch(
        `${backendUrl}/api/destroy-infrastructure/trigger`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            state_prefix: statePrefix,
          }),
        },
      );

      if (response.ok || response.status === 204) {
        setSnackbar({
          open: true,
          message:
            'Destroy workflow triggered successfully! Check GitHub Actions for progress.',
          severity: 'success',
        });
        setOpen(false);
      } else {
        const errorText = await response.text();
        throw new Error(
          `Failed to trigger destroy: ${response.status} - ${errorText}`,
        );
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTrigger isOpen={open} onOpenChange={setOpen}>
        <Button
          variant="secondary"
          destructive
          iconStart={<RiDeleteBinLine size={16} />}
          className={styles.trigger}
        >
          Destroy Infrastructure
        </Button>
        <Dialog>
          <DialogHeader>Destroy Infrastructure?</DialogHeader>
          <DialogBody>
            <Text variant="body-medium" as="p">
              Are you sure you want to destroy this infrastructure?
            </Text>
            <Text variant="body-medium" as="p">
              <strong>Environment:</strong> {envId}
              <br />
              <strong>Type:</strong> {templateType}
              <br />
              <strong>State Path:</strong> {statePrefix}
            </Text>
            <Text variant="body-medium" as="p">
              This action will:
            </Text>
            <ul className={styles.dialogList}>
              <li>Terminate all {getProviderName()} resources</li>
              <li>Remove the component from the catalog</li>
              <li>Cannot be undone</li>
            </ul>
          </DialogBody>
          <DialogFooter>
            <Button slot="close" variant="secondary" isDisabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              destructive
              isPending={loading}
              iconStart={<RiDeleteBinLine size={16} />}
              onPress={() => void handleDestroy()}
            >
              {loading ? 'Destroying...' : 'Destroy'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      {snackbar.open && (
        <div className={styles.snackbar}>
          <Alert
            status={snackbar.severity === 'success' ? 'success' : 'danger'}
            title={snackbar.message}
            customActions={
              <Button
                size="small"
                variant="tertiary"
                onPress={() => setSnackbar(prev => ({ ...prev, open: false }))}
              >
                Dismiss
              </Button>
            }
          />
        </div>
      )}
    </>
  );
};
